// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Review in the Bygning shell, as data: the two axes a type is judged on —
 * `review_status` (I kø / Godkendt / Fejldetektion) and the miljøstatus
 * badge — the Gennemgang queue, why Godkend is locked, what still blocks the
 * report. Pure: no React, no DOM. (The resource image is `sys:view`,
 * `kortlaegning/view.ts` — there is no ranked best-photo pick here.)
 */

import type { Sample, SurveyType } from '../api/types';
import { linkedSamples, pendingSampleList } from '../kortlaegning/samples';
import { ENV_LABEL, ENV_TONE, type Tone } from '../kortlaegning/vocab';

export interface Badge {
  label: string;
  tone: Tone;
}

export const REVIEW_LABEL: Record<SurveyType['review_status'], string> = {
  queue: 'I kø',
  approved: 'Godkendt',
  rejected: 'Fejldetektion',
};

const REVIEW_TONE: Record<SurveyType['review_status'], Tone> = {
  queue: 'wait',
  approved: 'good',
  rejected: 'crit',
};

/** The review pill. */
export function reviewPill(type: Pick<SurveyType, 'review_status'>): Badge {
  return { label: REVIEW_LABEL[type.review_status], tone: REVIEW_TONE[type.review_status] };
}

/**
 * The miljøstatus badge beside the pill: `Afventer P-01, P-03` (the open
 * samples' codes) while samples are out, `Forurenet`, `Ren (prøvesvar)`; none
 * for a type only screened (nothing to say). Labels and tones are
 * `ENV_LABEL`/`ENV_TONE`, except that an open sample reads in the warn tone
 * the status legend uses.
 */
export function envBadge(type: SurveyType, samples: readonly Sample[]): Badge | null {
  const s = type.environment_status;
  if (s === 'ren_screening') return null;
  if (s === 'afventer') {
    const codes = linkedSamples(type, [...samples])
      .filter((x) => x.stage !== 'svar')
      .map((x) => x.code);
    return { label: codes.length ? `Afventer ${codes.join(', ')}` : ENV_LABEL.afventer, tone: 'warn' };
  }
  return { label: ENV_LABEL[s], tone: ENV_TONE[s] };
}

/**
 * A strip card's status pill: the review status, except that a queued type
 * waiting for samples says so (it cannot be approved yet).
 */
export function stripStatus(type: Pick<SurveyType, 'review_status' | 'environment_status'>): Badge {
  if (type.review_status === 'queue' && type.environment_status === 'afventer') return { label: 'Afventer prøve', tone: 'warn' };
  return reviewPill(type);
}

/** The Gennemgang queue: every type still in review (an `afventer` one included — it waits for its samples). */
export function reviewQueue(types: readonly SurveyType[]): SurveyType[] {
  return types.filter((t) => t.review_status === 'queue');
}

/** Ressourcer's strip: everything but fejldetektion. */
export function stripTypes(types: readonly SurveyType[]): SurveyType[] {
  return types.filter((t) => t.review_status !== 'rejected');
}

/** The type to show after `id` leaves `list`: the next one, else the previous, else null. */
export function neighbourAfter(list: readonly Pick<SurveyType, 'id'>[], id: number): number | null {
  const at = list.findIndex((t) => t.id === id);
  if (at < 0) return list[0]?.id ?? null;
  return list[at + 1]?.id ?? list[at - 1]?.id ?? null;
}

/** The open samples' codes, joined ("P-01, P-03"); '' when none. */
export function openSampleCodes(type: SurveyType, samples: readonly Sample[]): string {
  return linkedSamples(type, [...samples])
    .filter((s) => s.stage !== 'svar')
    .map((s) => s.code)
    .join(', ');
}

/**
 * Why Godkend is locked, under the buttons ("Låst: afventer prøvesvar P-01"),
 * or null when it is not. The server's 422 says the same.
 */
export function approveLock(type: SurveyType, samples: readonly Sample[]): string | null {
  if (type.environment_status !== 'afventer') return null;
  const codes = openSampleCodes(type, samples);
  return codes ? `Låst: afventer prøvesvar ${codes}` : 'Låst: afventer prøvesvar';
}

/** The locked button's tooltip ("Kan ikke godkendes før P-01 · Tagpap, PAH har svar"). */
export function approveLockTitle(type: SurveyType, samples: readonly Sample[]): string | null {
  if (type.environment_status !== 'afventer') return null;
  const names = pendingSampleList(type, [...samples]);
  return names ? `Kan ikke godkendes før ${names} har svar` : 'Kan ikke godkendes før prøvesvaret er modtaget';
}

/**
 * "Mangler før rapport": what the report still needs from this type — EAK,
 * tons (not for bevaring, which leaves no waste), and screening when the
 * case has a Screenet field (`screened` null = no such field, not asked).
 */
export function missingBeforeReport(
  type: Pick<SurveyType, 'eak_code' | 'mass_t' | 'treatment'>,
  screened: boolean | null,
): string[] {
  const out: string[] = [];
  if (!type.eak_code.trim()) out.push('EAK');
  if (type.mass_t === null && type.treatment !== 'bevaring') out.push('tons');
  if (screened === false) out.push('screening');
  return out;
}
